import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FundClassification } from '@/models/common/FundClassification';
import Radio from '@/components/ui/Radio/Radio';
import OpenFundSection from './OpenFundSection';
import CoordinatorFields from './CoordinatorFields';
import {
  CoordinatorFormData,
  createDefaultCoordinator,
  OpenFundFormData,
  WelfareFormData,
} from '../schemas/openFundFormSchema';
import { useEditMode } from '../EditModeContext';
import { useOpenFundFieldTracking } from '../OpenFundAnalyticsContext';
import fieldStyles from './FormField.module.scss';

interface CoordinatorsSectionProps {
  classification: FundClassification.Welfare | FundClassification.Community;
}

const isCoordinatorEmpty = (coordinator: CoordinatorFormData): boolean => {
  const hasTextContent = [
    coordinator.firstName,
    coordinator.lastName,
    coordinator.israeliId,
    coordinator.city,
    coordinator.street,
    coordinator.houseNumber,
    coordinator.email,
    coordinator.phone,
    coordinator.relationToSupported,
    coordinator.spouseFirstName,
    coordinator.spouseIsraeliId,
  ].some((value) => (value ?? '').trim().length > 0);

  const hasFiles = Boolean(
    coordinator.idCardPhotocopy || coordinator.idAppendixPhotocopy
  );

  const hasAdditionalDocuments = (coordinator.additionalDocuments ?? []).some(
    (doc) => (doc?.name ?? '').trim().length > 0 || Boolean(doc?.file)
  );

  return !hasTextContent && !hasFiles && !hasAdditionalDocuments;
};

const CoordinatorsSection = ({ classification }: CoordinatorsSectionProps) => {
  const { t } = useTranslation();
  const isEditMode = useEditMode();
  const { control, setValue, getValues } = useFormContext<OpenFundFormData>();
  const trackField = useOpenFundFieldTracking();
  const isWelfare = classification === FundClassification.Welfare;

  const handleToggleChange = (nextIsCoordinator: boolean) => {
    trackField('isCoordinator');
    // In completion mode the coordinators are kept aligned with the server by index, so this choice
    // is locked to what was originally submitted.
    if (isEditMode) return;
    setValue(
      'isCoordinator' as never,
      nextIsCoordinator as never,
      { shouldValidate: false, shouldDirty: true }
    );
    const current =
      (getValues('coordinators') as WelfareFormData['coordinators']) || [];
    if (!nextIsCoordinator) {
      if (current.length === 0) {
        setValue('coordinators', [createDefaultCoordinator()], {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
      return;
    }
    const filledCoordinators = current.filter(
      (coordinator) => !isCoordinatorEmpty(coordinator)
    );
    if (filledCoordinators.length !== current.length) {
      setValue('coordinators', filledCoordinators, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  };

  if (!isWelfare) {
    return (
      <OpenFundSection title={t('openFundRequest.steps.applicantAndCoordinators')}>
        <CoordinatorFields />
      </OpenFundSection>
    );
  }

  return (
    <OpenFundSection title={t('openFundRequest.steps.applicantAndCoordinators')}>
      <Controller
        control={control}
        name={'isCoordinator' as never}
        render={({ field }) => {
          const value = field.value as boolean;
          return (
            <>
              <div className={fieldStyles.fieldWrapper}>
                <label className={`body-sm-regular ${fieldStyles.label}`}>
                  {t('openFundRequest.fields.isCoordinator')}
                </label>
                <div className="flex flex-row gap-6">
                  <label className="flex flex-row items-center gap-2 cursor-pointer">
                    <Radio
                      checked={value === true}
                      disabled={isEditMode}
                      onChange={() => handleToggleChange(true)}
                    />
                    <span className="body-md-regular">
                      {t('openFundRequest.boolean.yes')}
                    </span>
                  </label>
                  <label className="flex flex-row items-center gap-2 cursor-pointer">
                    <Radio
                      checked={value === false}
                      disabled={isEditMode}
                      onChange={() => handleToggleChange(false)}
                    />
                    <span className="body-md-regular">
                      {t('openFundRequest.boolean.no')}
                    </span>
                  </label>
                </div>
              </div>
              <CoordinatorFields
                allowRemoveLast={value}
                showRelationToSupported
              />
            </>
          );
        }}
      />
    </OpenFundSection>
  );
};

export default CoordinatorsSection;
